'use client';

import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { useLocale } from 'next-intl';
import { LocaleType } from '@/utils/constants';
import appCommunicationManager, {
  EAppCommunicationFuncName,
} from '@/utils/app-communication-manager';

/**
 * 切换 Shulex 显示状态
 */
function toggleShulex(show: boolean) {
  const chatbotElement = document.querySelector('shulex-chatbot-lancher') as HTMLElement;
  if (chatbotElement) {
    chatbotElement.style.display = show ? 'block' : 'none';
  }
  if (chatbotElement && chatbotElement.shadowRoot) {
    // 创建style标签，写入要修改的样式
    const style = document.createElement('style');
    style.innerHTML = `
      #livechat_launcher_btn {
        display: none;
      }
    `;

    // 将style标签插入Shadow DOM内部
    chatbotElement.shadowRoot.appendChild(style);
  }
}

interface UseCustomerServiceOptions {
  /**
   * 是否在 App 内嵌环境
   */
  inAppH5?: boolean;
}

interface UseCustomerServiceReturn {
  /**
   * 打开 Instadesk
   */
  openInstadesk: () => void;
  /**
   * 打开 Shulex
   */
  openShulex: () => void;
  /**
   * 关闭 Shulex
   */
  closeShulex: () => void;
  /**
   * 打开客服（根据 deskType 自动选择）
   */
  openCustomerService: () => void;
}

/**
 * 客服服务 Hook
 *
 * 提供打开客服的函数，状态管理通过全局 store 的 actions 操作
 */
export function useCustomerService(
  options: UseCustomerServiceOptions = {},
): UseCustomerServiceReturn {
  const { inAppH5 = false } = options;

  const userInfo = useStore((state) => state.userInfo);
  const customerService = useStore((state) => state.customerService);
  const setCustomerService = useStore((state) => state.setCustomerService);

  /**
   * 打开 Instadesk
   */
  const openInstadesk = () => {
    if (inAppH5) {
      appCommunicationManager.sendMessage(EAppCommunicationFuncName.LIVECHAT, { to: 'chat' });
      return;
    }
    setCustomerService({ showInstadesk: true });
  };

  /**
   * 打开 Shulex
   */

  const locale = useLocale() as LocaleType;
  const openShulex = () => {
    if (inAppH5) {
      appCommunicationManager.sendMessage(EAppCommunicationFuncName.LIVECHAT, { to: 'chat' });
      return;
    }
    logger.info('locale++++++', locale);
    window.__shulexConfig = {
      email: userInfo?.email || '',
      lang: locale,
    };
    if (window.__shulexLiveChat) {
      setCustomerService({ showShulexCloseIcon: true });
      window.__shulexLiveChat?.open();
      toggleShulex(true);
    }
  };

  /**
   * 关闭 Shulex
   */
  const closeShulex = () => {
    toggleShulex(false);
    setCustomerService({ showShulexCloseIcon: false });
    window.__shulexLiveChat?.close();
  };

  /**
   * 打开客服（根据 deskType 自动选择）
   */
  const openCustomerService = () => {
    if (customerService.deskType === 'instadesk') {
      openInstadesk();
      return;
    }
    if (customerService.deskType === 'shulex') {
      openShulex();
      return;
    }
    // Zendesk
    if (typeof window !== 'undefined' && (window as any).zE) {
      try {
        (window as any).zE('messenger', 'open');
      } catch (error) {
        logger.error('Zendesk error:', error);
      }
    }
  };

  return {
    openInstadesk,
    openShulex,
    closeShulex,
    openCustomerService,
  };
}
